import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import workOrderService, { type BoqBomLineOption } from '../../services/workOrder.service';
import bomService, {
  type BomCreatePayload,
  type BomLineDto,
  type BomLinkedWorkOrderRow,
  type BomOperationDto,
} from '../../services/bom.service';
import operationService, { type Operation } from '../../services/operation.service';
import api from '../../services/api.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useBoqBomLineOptions } from '../../hooks/useWorkOrder';
import { useItems, useUomUnits } from '../../hooks/useInventory';
import { ItemSelect } from '../../components/inventory/ItemSelect';

const { Title, Text } = Typography;
const LIST_PATH = '/mfg/boms';

function operationLineCost(op: Pick<
  BomOperationDto,
  | 'setup_time_hours'
  | 'run_time_per_unit_hours'
  | 'labor_rate_per_hour'
  | 'overhead_rate_per_hour'
  | 'machine_rate_per_hour'
  | 'tooling_cost'
  | 'is_outsourced'
  | 'outsource_cost'
>): number {
  if (op.is_outsourced && Number(op.outsource_cost) > 0) {
    return Math.round(Number(op.outsource_cost) * 100) / 100;
  }
  const hours = (Number(op.setup_time_hours) || 0) + (Number(op.run_time_per_unit_hours) || 0);
  const labor = hours * (Number(op.labor_rate_per_hour) || 0);
  const overhead = hours * (Number(op.overhead_rate_per_hour) || 0);
  const machine = hours * (Number(op.machine_rate_per_hour) || 0);
  return Math.round((labor + overhead + machine + (Number(op.tooling_cost) || 0)) * 100) / 100;
}

function blankOperation(sequence: number): BomOperationDto {
  return {
    id: `temp-op-${Date.now()}-${sequence}`,
    operation_id: '',
    description: '',
    sequence_number: sequence,
    setup_time_hours: 0,
    run_time_per_unit_hours: 0,
    teardown_time_hours: 0,
    labor_rate_per_hour: 0,
    overhead_rate_per_hour: 0,
    machine_rate_per_hour: 0,
    tooling_cost: 0,
    is_outsourced: false,
    outsource_cost: null,
    outsourced_scope_detail: '',
    outsourced_supplier_category_ids: [],
    requires_quality_check: false,
    notes: '',
    total_cost: 0,
  };
}

function operationFromEstimate(
  op: NonNullable<BoqBomLineOption['operations']>[number],
  index: number
): BomOperationDto {
  return {
    id: `boq-op-${op.operation_id}-${index}`,
    operation_id: op.operation_id,
    description: op.description,
    sequence_number: op.sequence_number || index + 1,
    setup_time_hours: op.setup_time_hours,
    run_time_per_unit_hours: op.run_time_per_unit_hours,
    teardown_time_hours: 0,
    labor_rate_per_hour: op.labor_rate_per_hour,
    overhead_rate_per_hour: op.overhead_rate_per_hour,
    machine_rate_per_hour: op.machine_rate_per_hour,
    tooling_cost: op.tooling_cost,
    is_outsourced: op.is_outsourced,
    outsource_cost: op.outsource_cost,
    outsourced_scope_detail: op.outsourced_scope_detail,
    outsourced_supplier_category_ids: op.outsourced_supplier_category_ids ?? [],
    requires_quality_check: false,
    notes: op.notes,
    total_cost: 0,
  };
}

const BOMFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [bomItems, setBomItems] = useState<BomLineDto[]>([]);
  const [bomOperations, setBomOperations] = useState<BomOperationDto[]>([]);
  const { formatCurrency } = useCurrency();

  const bomQuery = useQuery({
    queryKey: ['manufacturing-boms', id],
    queryFn: () => bomService.getById(id!),
    enabled: isEdit,
  });
  const selectedBOM = bomQuery.data;

  const linkedWosQuery = useQuery({
    queryKey: ['manufacturing-boms', id, 'linked-work-orders'],
    queryFn: () => bomService.getLinkedWorkOrders(id!),
    enabled: isEdit,
  });

  const createMutation = useMutation({
    mutationFn: (payload: BomCreatePayload) => bomService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturing-boms'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: BomCreatePayload) => bomService.update(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturing-boms'] });
    },
  });

  const linkedWorkOrderId = Form.useWatch('bom_link_work_order_id', form) as string | undefined;

  const workOrdersQuery = useQuery({
    queryKey: ['work-orders', 'bom-create'],
    queryFn: () => workOrderService.getAll(),
    enabled: !isEdit,
  });

  const boqLineOptionsQuery = useBoqBomLineOptions(linkedWorkOrderId ?? '', {
    enabled: !isEdit && !!linkedWorkOrderId,
  });

  const { data: inventoryItems = [], isLoading: inventoryItemsLoading } = useItems();
  const { data: uomMasterRows = [] } = useUomUnits({ all: true });

  const operationsMasterQuery = useQuery({
    queryKey: ['manufacturing-operations', 'bom-form'],
    queryFn: async () => {
      let companyId = '';
      try {
        const raw = localStorage.getItem('user');
        if (raw) companyId = (JSON.parse(raw) as { company_id?: string }).company_id || '';
      } catch {
        companyId = '';
      }
      return operationService.getAll(companyId, true);
    },
  });
  const operationsMaster: Operation[] = operationsMasterQuery.data ?? [];

  const supplierCategoriesQuery = useQuery({
    queryKey: ['procurement-supplier-categories'],
    queryFn: async () => {
      const res = await api.get<Array<{ id: string; category_name: string }>>('/procurement/supplier-categories');
      const rows = res.data;
      return Array.isArray(rows) ? rows : [];
    },
  });

  const inventoryUomOptions = useMemo(() => {
    const set = new Set<string>(['PCS', 'SET', 'M', 'KG', 'SQM', 'L', 'EA', 'NOS']);
    uomMasterRows.forEach((row) => {
      const u = row.code?.trim();
      if (u && row.is_active !== false) set.add(u);
    });
    inventoryItems.forEach((it) => {
      const u = it.base_uom?.trim();
      if (u) set.add(u);
    });
    bomItems.forEach((b) => {
      const u = b.uom?.trim();
      if (u) set.add(u);
    });
    if (selectedBOM?.uom?.trim()) set.add(selectedBOM.uom.trim());
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [inventoryItems, bomItems, selectedBOM, uomMasterRows]);

  useEffect(() => {
    if (!selectedBOM) return;
    setBomItems(selectedBOM.items ?? []);
    setBomOperations(selectedBOM.operations ?? []);
    form.setFieldsValue({
      bom_name: selectedBOM.bom_name,
      description: selectedBOM.description ?? undefined,
      item_id: selectedBOM.item_id,
      quantity: selectedBOM.quantity,
      uom: selectedBOM.uom,
      is_active: selectedBOM.is_active,
      is_default: selectedBOM.is_default,
    });
  }, [selectedBOM, form]);

  const handleAddItem = () => {
    setBomItems((prev) => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        item_id: '',
        item_name: '',
        quantity: 1,
        uom: 'PCS',
        scrap_percentage: 0,
      },
    ]);
  };

  const handleRemoveItem = (lineId: string) => {
    setBomItems((prev) => prev.filter((item) => item.id !== lineId));
  };

  const patchOperation = (index: number, patch: Partial<BomOperationDto>) => {
    setBomOperations((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const applyOperationMaster = (index: number, operationId: string) => {
    const master = operationsMaster.find((op) => op.id === operationId);
    patchOperation(index, {
      operation_id: operationId,
      description: master?.description || master?.operation_name || '',
      setup_time_hours: Number(master?.standard_setup_time_hours) || 0,
      run_time_per_unit_hours: Number(master?.standard_run_time_per_unit_hours) || 0,
      teardown_time_hours: Number(master?.standard_teardown_time_hours) || 0,
      labor_rate_per_hour: Number(master?.per_hour_cost) || 0,
    });
  };

  const handleSubmit = async (values: {
    bom_name: string;
    description?: string;
    item_id: string;
    quantity: number;
    uom: string;
    is_active?: boolean;
    is_default?: boolean;
    bom_link_work_order_id?: string;
    bom_link_work_order_line_item_id?: string;
  }) => {
    if (bomItems.length < 1) {
      message.error('Add at least one component line');
      return;
    }
    if (bomItems.some((i) => !i.item_id)) {
      message.error('Select an inventory item for every component line');
      return;
    }
    if (bomOperations.some((op) => !op.operation_id)) {
      message.error('Select an operation from Operations Master for every routing line');
      return;
    }
    const payload: BomCreatePayload = {
      bom_name: values.bom_name,
      description: values.description?.trim() || null,
      item_id: values.item_id,
      quantity: values.quantity,
      uom: values.uom,
      is_active: values.is_active !== false,
      is_default: !!values.is_default,
      items: bomItems.map((b) => ({
        item_id: b.item_id,
        quantity: b.quantity,
        uom: b.uom,
        scrap_percentage: b.scrap_percentage,
        notes: b.notes?.trim() || null,
      })),
      operations: bomOperations.map((op, index) => ({
        operation_id: op.operation_id,
        description: op.description?.trim() || null,
        sequence_number: op.sequence_number || index + 1,
        setup_time_hours: op.setup_time_hours,
        run_time_per_unit_hours: op.run_time_per_unit_hours,
        teardown_time_hours: op.teardown_time_hours,
        labor_rate_per_hour: op.labor_rate_per_hour,
        overhead_rate_per_hour: op.overhead_rate_per_hour,
        machine_rate_per_hour: op.machine_rate_per_hour,
        tooling_cost: op.tooling_cost,
        is_outsourced: op.is_outsourced,
        outsource_cost: op.is_outsourced ? op.outsource_cost : null,
        outsourced_scope_detail: op.is_outsourced ? op.outsourced_scope_detail : null,
        outsourced_supplier_category_ids: op.is_outsourced ? op.outsourced_supplier_category_ids ?? [] : [],
        requires_quality_check: op.requires_quality_check,
        notes: op.notes?.trim() || null,
      })),
    };
    if (!isEdit) {
      payload.link_work_order_id = String(values.bom_link_work_order_id ?? '');
      payload.link_work_order_line_item_id = String(values.bom_link_work_order_line_item_id ?? '');
    }
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(payload);
        message.success('BOM updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        message.success('BOM created successfully');
      }
      navigate(LIST_PATH);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Error saving BOM';
      message.error(msg);
    }
  };

  const linkedWoLinkTypeLabel: Record<BomLinkedWorkOrderRow['link_type'], string> = {
    bom_assigned: 'BOM on work order',
    same_finished_item: 'Same finished item',
    wo_code_matches_bom_number: 'WO item code = BOM #',
  };

  const linkedWorkOrderColumns: ColumnsType<BomLinkedWorkOrderRow> = [
    {
      title: 'Work order',
      key: 'wo',
      render: (_, row) => <Link to={`/mfg/work-orders/${row.id}`}>{row.work_order_number}</Link>,
    },
    { title: 'Status', dataIndex: 'work_order_status', key: 'work_order_status', width: 130 },
    {
      title: 'Planned qty',
      key: 'pq',
      width: 110,
      render: (_, row) => `${row.planned_quantity} ${row.uom}`,
    },
    {
      title: 'Item on WO',
      key: 'item',
      ellipsis: true,
      render: (_, row) => {
        const parts = [row.item_code, row.item_name].filter(Boolean);
        return parts.length ? parts.join(' — ') : '—';
      },
    },
    {
      title: 'How linked',
      dataIndex: 'link_type',
      key: 'link_type',
      width: 200,
      render: (t: BomLinkedWorkOrderRow['link_type']) => linkedWoLinkTypeLabel[t] ?? t,
    },
  ];

  const itemColumns: ColumnsType<BomLineDto> = [
    {
      title: 'Item',
      dataIndex: 'item_name',
      key: 'item_name',
      render: (_, record, index) => (
        <ItemSelect
          placeholder={inventoryItemsLoading ? 'Loading items…' : 'Select item'}
          style={{ width: '100%' }}
          items={inventoryItems}
          autoLoad={false}
          loading={inventoryItemsLoading}
          value={record.item_id || undefined}
          onChange={(value) => {
            const updated = [...bomItems];
            updated[index].item_id = value as string;
            const inv = inventoryItems.find((i) => i.id === value);
            updated[index].item_name = inv ? `${inv.item_code} — ${inv.item_name}` : '';
            const u = inv?.base_uom?.trim();
            if (u) updated[index].uom = u;
            setBomItems(updated);
          }}
        />
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          min={0.01}
          step={0.01}
          value={record.quantity}
          onChange={(value) => {
            const updated = [...bomItems];
            updated[index].quantity = value || 0;
            setBomItems(updated);
          }}
        />
      ),
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      width: 120,
      render: (_, record, index) => (
        <Select
          showSearch
          optionFilterProp="label"
          value={record.uom || undefined}
          options={inventoryUomOptions.map((u) => ({ value: u, label: u }))}
          onChange={(value) => {
            const updated = [...bomItems];
            updated[index].uom = value;
            setBomItems(updated);
          }}
        />
      ),
    },
    {
      title: 'Scrap %',
      dataIndex: 'scrap_percentage',
      key: 'scrap_percentage',
      width: 100,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          max={100}
          value={record.scrap_percentage}
          onChange={(value) => {
            const updated = [...bomItems];
            updated[index].scrap_percentage = value || 0;
            setBomItems(updated);
          }}
        />
      ),
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 180,
      render: (_, record, index) => (
        <Input
          value={record.notes ?? ''}
          placeholder="Optional"
          onChange={(event) => {
            const updated = [...bomItems];
            updated[index].notes = event.target.value;
            setBomItems(updated);
          }}
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleRemoveItem(record.id)} />
      ),
    },
  ];

  const hoursInput = (value: number, onChange: (next: number) => void) => (
    <InputNumber min={0} step={0.01} value={value} style={{ width: 88 }} onChange={(next) => onChange(Number(next) || 0)} />
  );

  const operationColumns: ColumnsType<BomOperationDto> = [
    {
      title: '#',
      dataIndex: 'sequence_number',
      width: 70,
      render: (_, record, index) => (
        <InputNumber
          min={1}
          value={record.sequence_number}
          style={{ width: 60 }}
          onChange={(value) => patchOperation(index, { sequence_number: Number(value) || index + 1 })}
        />
      ),
    },
    {
      title: 'Operation',
      width: 220,
      render: (_, record, index) => (
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Operations Master"
          style={{ width: '100%' }}
          loading={operationsMasterQuery.isPending}
          value={record.operation_id || undefined}
          options={operationsMaster.map((op) => ({
            value: op.id,
            label: `${op.operation_code} — ${op.operation_name}`,
          }))}
          onChange={(value) => applyOperationMaster(index, value)}
        />
      ),
    },
    {
      title: 'Description',
      width: 180,
      render: (_, record, index) => (
        <Input
          value={record.description ?? ''}
          onChange={(event) => patchOperation(index, { description: event.target.value })}
        />
      ),
    },
    {
      title: 'Setup h',
      width: 100,
      render: (_, record, index) => hoursInput(record.setup_time_hours, (value) => patchOperation(index, { setup_time_hours: value })),
    },
    {
      title: 'Run h',
      width: 100,
      render: (_, record, index) =>
        hoursInput(record.run_time_per_unit_hours, (value) => patchOperation(index, { run_time_per_unit_hours: value })),
    },
    {
      title: 'Teardown h',
      width: 110,
      render: (_, record, index) =>
        hoursInput(record.teardown_time_hours, (value) => patchOperation(index, { teardown_time_hours: value })),
    },
    {
      title: 'Labor / h',
      width: 100,
      render: (_, record, index) =>
        hoursInput(record.labor_rate_per_hour, (value) => patchOperation(index, { labor_rate_per_hour: value })),
    },
    {
      title: 'Overhead / h',
      width: 110,
      render: (_, record, index) =>
        hoursInput(record.overhead_rate_per_hour, (value) => patchOperation(index, { overhead_rate_per_hour: value })),
    },
    {
      title: 'Machine / h',
      width: 110,
      render: (_, record, index) =>
        hoursInput(record.machine_rate_per_hour, (value) => patchOperation(index, { machine_rate_per_hour: value })),
    },
    {
      title: 'Tooling',
      width: 100,
      render: (_, record, index) => hoursInput(record.tooling_cost, (value) => patchOperation(index, { tooling_cost: value })),
    },
    {
      title: 'QC',
      width: 70,
      render: (_, record, index) => (
        <Switch
          checked={record.requires_quality_check}
          onChange={(checked) => patchOperation(index, { requires_quality_check: checked })}
        />
      ),
    },
    {
      title: 'Outsource',
      width: 100,
      render: (_, record, index) => (
        <Switch checked={record.is_outsourced} onChange={(checked) => patchOperation(index, { is_outsourced: checked })} />
      ),
    },
    {
      title: 'Outsource cost',
      width: 130,
      render: (_, record, index) =>
        record.is_outsourced ? (
          <InputNumber
            min={0}
            value={record.outsource_cost ?? 0}
            style={{ width: 110 }}
            onChange={(value) => patchOperation(index, { outsource_cost: Number(value) || 0 })}
          />
        ) : (
          '—'
        ),
    },
    {
      title: 'Scope',
      width: 180,
      render: (_, record, index) =>
        record.is_outsourced ? (
          <Input
            value={record.outsourced_scope_detail ?? ''}
            placeholder="Vendor scope"
            onChange={(event) => patchOperation(index, { outsourced_scope_detail: event.target.value })}
          />
        ) : (
          '—'
        ),
    },
    {
      title: 'Supplier categories',
      width: 220,
      render: (_, record, index) =>
        record.is_outsourced ? (
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            loading={supplierCategoriesQuery.isPending}
            value={record.outsourced_supplier_category_ids ?? []}
            options={(supplierCategoriesQuery.data ?? []).map((category) => ({
              value: category.id,
              label: category.category_name,
            }))}
            onChange={(value) => patchOperation(index, { outsourced_supplier_category_ids: value })}
          />
        ) : (
          '—'
        ),
    },
    {
      title: 'Notes',
      width: 160,
      render: (_, record, index) => (
        <Input value={record.notes ?? ''} onChange={(event) => patchOperation(index, { notes: event.target.value })} />
      ),
    },
    {
      title: 'Cost',
      width: 110,
      render: (_, record) => formatCurrency(operationLineCost(record)),
    },
    {
      title: '',
      width: 50,
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => setBomOperations((prev) => prev.filter((row) => row.id !== record.id))}
        />
      ),
    },
  ];

  const materialCost = bomItems.reduce((sum, line) => {
    const item = inventoryItems.find((row) => row.id === line.item_id);
    const scrap = 1 + (Number(line.scrap_percentage) || 0) / 100;
    return sum + (Number(item?.standard_cost) || 0) * Number(line.quantity) * scrap;
  }, 0);
  const routingCost = bomOperations.reduce((sum, op) => sum + operationLineCost(op), 0);

  if (isEdit && bomQuery.isPending) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isEdit && (bomQuery.isError || !selectedBOM)) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="BOM not found"
          action={
            <Button onClick={() => navigate(LIST_PATH)}>Back to list</Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(LIST_PATH)} style={{ marginBottom: 16 }}>
        Bill of Materials
      </Button>
      <Title level={3} style={{ marginTop: 0 }}>
        {isEdit ? `Edit BOM${selectedBOM?.bom_code ? ` ${selectedBOM.bom_code}` : ''}` : 'Create BOM'}
      </Title>

      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {!isEdit ? (
            <>
              <Alert
                type="info"
                showIcon
                message="Work order and BOQ line are required"
                description="Select a work order, then the BOQ line from the estimate. BOM name, quantity, and UOM can fill from that line; choose the finished inventory item manually."
                style={{ marginBottom: 16 }}
              />
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="bom_link_work_order_id"
                    label="Work order"
                    rules={[{ required: true, message: 'Please select a work order' }]}
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select work order"
                      loading={workOrdersQuery.isPending}
                      options={(workOrdersQuery.data ?? []).map((wo) => ({
                        value: wo.id,
                        label: `${wo.work_order_number}${wo.sales_order_number ? ` · SO ${wo.sales_order_number}` : ''}`,
                      }))}
                      onChange={() => {
                        form.setFieldsValue({
                          bom_link_work_order_line_item_id: undefined,
                          bom_name: undefined,
                          quantity: 1,
                          uom: 'PCS',
                        });
                        setBomOperations([]);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="bom_link_work_order_line_item_id"
                    label="BOQ line (from estimate)"
                    rules={[{ required: true, message: 'Please select a BOQ line' }]}
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={
                        !linkedWorkOrderId
                          ? 'Select a work order first'
                          : boqLineOptionsQuery.isPending
                            ? 'Loading lines…'
                            : (boqLineOptionsQuery.data?.length ?? 0) === 0
                              ? 'No BOQ lines linked to this WO'
                              : 'Select BOQ / WO line'
                      }
                      disabled={!linkedWorkOrderId}
                      loading={boqLineOptionsQuery.isPending}
                      options={(boqLineOptionsQuery.data ?? []).map((opt) => ({
                        value: opt.work_order_line_item_id,
                        label: `WO L${opt.work_order_line_number} · ${opt.bom_name}`,
                      }))}
                      onChange={(lineItemId: string | null) => {
                        if (!lineItemId) return;
                        const opt = boqLineOptionsQuery.data?.find((o) => o.work_order_line_item_id === lineItemId);
                        if (opt) {
                          form.setFieldsValue({
                            bom_name: opt.bom_name,
                            quantity: opt.planned_quantity,
                            uom: opt.uom,
                          });
                          setBomOperations((opt.operations ?? []).map(operationFromEstimate));
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          ) : null}

          {isEdit ? (
            <>
              <Divider orientation="left">Work order detail</Divider>
              {linkedWosQuery.isPending ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <Spin />
                </div>
              ) : linkedWosQuery.isError ? (
                <Alert type="warning" showIcon message="Could not load work orders for this BOM" style={{ marginBottom: 16 }} />
              ) : (linkedWosQuery.data?.work_orders?.length ?? 0) === 0 ? (
                <Alert
                  type="info"
                  showIcon
                  message="No work orders linked to this BOM yet"
                  description={
                    <>
                      Assign this BOM on the work order (BOM field), or ensure the work order uses the same finished item / item code as this BOM.{' '}
                      <Link to="/mfg/work-orders">Open work orders</Link>
                    </>
                  }
                  style={{ marginBottom: 16 }}
                />
              ) : (
                <Table<BomLinkedWorkOrderRow>
                  size="small"
                  rowKey="id"
                  pagination={linkedWosQuery.data!.work_orders.length > 8 ? { pageSize: 8 } : false}
                  dataSource={linkedWosQuery.data!.work_orders}
                  columns={linkedWorkOrderColumns}
                  style={{ marginBottom: 16 }}
                />
              )}
            </>
          ) : null}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="BOM Name" name="bom_name" rules={[{ required: true, message: 'Please enter BOM name' }]}>
                <Input placeholder="Filled from BOQ line description when linked, or type manually" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Finished Item" name="item_id" rules={[{ required: true, message: 'Please select item' }]}>
                <ItemSelect
                  placeholder="Select finished good"
                  items={inventoryItems}
                  autoLoad={false}
                  loading={inventoryItemsLoading}
                  onChange={(itemId) => {
                    const inv = inventoryItems.find((i) => i.id === itemId);
                    const u = inv?.base_uom?.trim();
                    if (u) form.setFieldsValue({ uom: u });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Quantity" name="quantity" initialValue={1} rules={[{ required: true }]}>
                <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="UOM" name="uom" initialValue="PCS" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="From item or choose"
                  options={inventoryUomOptions.map((u) => ({ value: u, label: u }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Status" name="is_active" initialValue={true}>
                <Select
                  options={[
                    { value: true, label: 'Active' },
                    { value: false, label: 'Inactive' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="How this BOM is used, revision notes, or process remarks" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Default BOM for finished item" name="is_default" initialValue={false}>
                <Select
                  options={[
                    { value: false, label: 'No' },
                    { value: true, label: 'Yes' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Components</Divider>

          <Table columns={itemColumns} dataSource={bomItems} rowKey="id" pagination={false} size="small" />

          <Button type="dashed" onClick={handleAddItem} block icon={<PlusOutlined />} style={{ marginTop: 16, marginBottom: 16 }}>
            Add Component
          </Button>

          <Divider>Operations</Divider>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Routing comes from the estimate line"
            description="Selecting a BOQ line copies its operations, rates, tooling, and outsource scope. Edit them here before saving. Cost is setup plus run hours times labor, overhead, and machine rates, plus tooling. An outsourced line uses the outsource amount instead. Scrap is included in the material cost."
          />
          <Table
            columns={operationColumns}
            dataSource={bomOperations}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 2100 }}
          />
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            style={{ marginTop: 16, marginBottom: 16 }}
            onClick={() => setBomOperations((prev) => [...prev, blankOperation(prev.length + 1)])}
          >
            Add Operation
          </Button>

          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message={`BOM cost ${formatCurrency(materialCost + routingCost)}`}
            description={
              <Text>
                Materials {formatCurrency(materialCost)} (quantity × standard cost, including scrap) + operations{' '}
                {formatCurrency(routingCost)}
              </Text>
            }
          />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createMutation.isPending || updateMutation.isPending}>
                {isEdit ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => navigate(LIST_PATH)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default BOMFormPage;
